import { useState } from "react";
import { SERVICE_ACTIVITY_CATEGORY_LABEL } from "@shared/serviceActivityCategory";
import { roActivityWindow, sortRoActivityNewestFirst } from "@shared/roActivityTimeline";
import { presentRoAuditEntry } from "@/lib/roWorkAuditLog";
import { ACTIVITY_BADGE_CLASS, ACTIVITY_DOT_CLASS } from "./activityCategoryStyles";

export type RoActivityEntry = {
  id: number;
  action: string;
  newValue?: unknown;
  createdAt: string | Date;
  performedBy: string;
};

export function RoActivityTimeline({
  entries,
  formatDateTime,
}: {
  entries: RoActivityEntry[];
  formatDateTime: (value: string | Date) => string;
}) {
  const [expanded, setExpanded] = useState(false);
  const ordered = sortRoActivityNewestFirst(entries);
  const windowed = roActivityWindow(ordered, expanded);

  if (ordered.length === 0) {
    return <p className="mt-4 text-sm text-slate-500">No activity recorded yet.</p>;
  }

  return (
    <div className="mt-4">
      <ol className="space-y-4">
        {windowed.visible.map((entry) => {
          const presentation = presentRoAuditEntry(entry, (value) => formatDateTime(value));
          return (
            <li key={entry.id} data-testid="ro-activity-entry" data-category={presentation.category} className="flex gap-3 text-sm">
              <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${ACTIVITY_DOT_CLASS[presentation.category]}`} aria-hidden />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-medium text-slate-900">{presentation.action}</p>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${ACTIVITY_BADGE_CLASS[presentation.category]}`}>
                    {SERVICE_ACTIVITY_CATEGORY_LABEL[presentation.category]}
                  </span>
                </div>
                <p className="text-xs text-slate-500">{formatDateTime(entry.createdAt)}</p>
                <p className="text-xs text-slate-600">{entry.performedBy}</p>
                {presentation.detail ? <p className="mt-1 text-xs text-slate-600">{presentation.detail}</p> : null}
              </div>
            </li>
          );
        })}
      </ol>
      {windowed.canExpand ? (
        <button
          type="button"
          data-testid="ro-activity-show-older"
          className="mt-4 text-sm font-medium text-slate-700 underline-offset-2 hover:underline"
          onClick={() => setExpanded(true)}
        >
          {windowed.expandLabel}
        </button>
      ) : null}
      {windowed.canCollapse ? (
        <button
          type="button"
          data-testid="ro-activity-show-less"
          className="mt-4 text-sm font-medium text-slate-700 underline-offset-2 hover:underline"
          onClick={() => setExpanded(false)}
        >
          {windowed.collapseLabel}
        </button>
      ) : null}
    </div>
  );
}
